// Ficha do profissional — o miolo do hub RH (2026-08-25).
//
// Molde: NegocioDetalhe (coluna esquerda com as seções da pessoa, coluna
// direita com Tabs size=small e corpo por &&). A fonte é a action `rh_ficha`
// (comercial-engine handlers/rh.js); documentos continuam no motor do acervo
// (docsRH.tsx + administrativo-documentos/api) e salário no dre-engine.
//
// Permissões — quem decide é o backend, a tela só esconde botão que
// responderia 403:
//  - 403 no rh_ficha → "Você não tem acesso" (nunca ficha vazia);
//  - `remuneracao` dos períodos de férias SÓ vem na resposta para quem tem
//    financeiro.profissionais.ver — a coluna aparece quando o campo veio;
//  - a aba Remuneração usa o mesmo sinal da tela antiga: a presença da chave
//    `salario_esperado` no objeto do GET /profissionais (ficha podada no
//    servidor, não 403 — 403 confirmaria que o campo existe).
import { ReactElement, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert, Button, Card, Col, DatePicker, Descriptions, Empty, Flex, Grid, Input,
  InputNumber, Modal, Popconfirm, Row, Select, Space, Spin, Statistic, Switch,
  Table, Tabs, Tag, Tooltip, Typography, App as AntApp,
} from 'antd';
import {
  ArrowLeftOutlined, EditOutlined, DeleteOutlined, PlusOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { fmtDataBr, dataAoMeioDia } from '../../utils/dataFmt';
import type { Documento, SubcategoriaDoc } from '../administrativo-documentos/types';
import { listarDocumentos, listarSubcategorias } from '../administrativo-documentos/api';
import type { FiltrosDoc } from '../administrativo-documentos/api';
import {
  PendenciaRH, PessoaRH, fmtPendencia, colunasDocsRH, baixarDocRH,
  arquivarDocRH, ModalNovoDocRH, ModalEditarDocRH,
} from './docsRH';
import SecaoCadastro, { ClockifyCfg, Profissional } from './SecaoCadastro';
import OrganogramaCampos from '../../components/pessoas/OrganogramaCampos';
import HistoricoRemuneracao from '../profissionais/HistoricoRemuneracao';
import MinhaSemana from '../operacional-horas/MinhaSemana';
import TotalHoras from '../operacional-horas/TotalHoras';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

// ── Transporte das actions rh_* (POST /comercial/propostas-geradas) ────────
// Mesma rota e mesmo desembrulho do body das outras actions do comercial.
export async function acaoRH<T = any>(action: string, body: Record<string, any> = {}): Promise<T> {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
    { action, ...body }, { headers: { Authorization: token } });
  return r.data.body ? JSON.parse(r.data.body) : r.data;
}

/** Uma pessoa do GET /financeiro/horas/cobertura (dre-engine). Daqui só se usa o que é
 *  fato do mês (o anotado e os dias de ausência): "Esperado" e "Cobertura" saíram em
 *  04/10/2026 — eram a conta antiga (mês inteiro como régua, sem Clockify, sem rateio
 *  fixo) e contradiziam o painel Horas da equipe, que é o lugar desse veredito. */
type CoberturaPessoa = {
  profissional_id: string;
  usuario_email: string;
  minutos: number;
  dias_ausencia: number;
};

export const msgErro = (e: any): string =>
  e?.response?.data?.error || e?.response?.data?.erro || e?.message || 'erro desconhecido';

// ── Tipos da action rh_ficha (handlers/rh.js › rhFicha) ────────────────────
type PessoaFicha = {
  id: string;
  nome: string;
  ativo: boolean;
  usuario_email: string | null;
  cargo: string | null;
  cpf: string | null;                  // 11 dígitos, sem máscara
  data_nascimento: string | null;
  endereco: string | null;
  contato_emergencia: string | null;
  tipo_sanguineo: string | null;
  alergias: string | null;
  contrato_tipo: string | null;
  setor: string | null;
  setor_id?: string | null;
  chefe_profissional_id?: string | null;
  chefe_nome?: string | null;
  regime: string | null;
  admissao: string | null;
  desligamento: string | null;
  atualizado_em: string | null;
  atualizado_por: string | null;
};

type PeriodoFerias = {
  id: string;
  inicio: string;
  fim: string;
  dias: number | string;               // numeric do PG chega como string
  observacao: string | null;
  criado_por: string | null;
  criado_em: string | null;
  /** SÓ vem na resposta com financeiro.profissionais.ver. */
  remuneracao?: number | string | null;
};

type Ficha = {
  pessoa: PessoaFicha;
  ferias: {
    direito: number | string | null;   // null = sem data de admissão
    gozados: number | string;
    saldo: number | string | null;
    periodos: PeriodoFerias[];
  };
  docs: { total: number | string; pendencias: PendenciaRH[] };
  pode_gerenciar: boolean;
  pode_editar_ficha: boolean;
};

export interface RecursosFicha {
  /** GET /profissionais?incluir_inativos=1 (dre-engine, array cru). */
  profissionais: Profissional[];
  /** action profissional_clockify_listar, mapa por id. */
  clockifyCfgMap: Record<string, ClockifyCfg>;
  /** action clockify_listar_user_map. */
  clockifyUsers: any[];
  /** GET /admin/usuarios (pessoas com login). */
  usuariosSistema: { email: string; nome: string }[];
}

const fmtCpf = (c: string | null | undefined): string => {
  const d = String(c || '').replace(/\D/g, '');
  if (d.length !== 11) return c || '—';
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};

const fmtDias = (v: number | string | null | undefined): string => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
};

const brl = (v: number | string | null | undefined): string => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

/** Dias corridos do período, pontas inclusas (fim − início + 1). */
const diasCorridos = (inicio: string, fim: string): number | null => {
  const a = dataAoMeioDia(inicio);
  const b = dataAoMeioDia(fim);
  if (!a || !b) return null;
  return Math.round((b.getTime() - a.getTime()) / 86400000) + 1;
};

// Seção da coluna esquerda — o SecaoSidebar do NegocioDetalhe, sem os botões.
function Secao({ titulo, ultima, children }: {
  titulo: string; ultima?: boolean; children: ReactNode;
}): ReactElement {
  return (
    <div style={{ padding: '10px 12px', borderBottom: ultima ? undefined : '1px solid #f0f0f0' }}>
      <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {titulo}
      </Text>
      <div style={{ marginTop: 6 }}>{children}</div>
    </div>
  );
}

const descProps = {
  size: 'small' as const, column: 1, colon: false,
  labelStyle: { color: '#888', fontSize: 11, width: 92 },
  contentStyle: { fontSize: 12 },
};

// ── Modal de edição da ficha (rh_ficha_salvar) ─────────────────────────────
const TIPOS_SANGUINEOS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

function ModalEditarFicha({ pessoa, onClose, onSaved }: {
  pessoa: PessoaFicha;
  onClose: () => void;
  onSaved: () => void;
}): ReactElement {
  const { message } = AntApp.useApp();
  const d10 = (v: string | null) => (v ? String(v).slice(0, 10) : '');
  // Setor e data de saída NÃO se editam aqui (28/09/2026): o setor é do
  // organograma (OrganogramaCampos) e a saída é o botão Desligar da ficha.
  const [f, setF] = useState({
    cargo: pessoa.cargo || '',
    contrato_tipo: pessoa.contrato_tipo || '',
    regime: pessoa.regime || '',
    cpf: pessoa.cpf || '',
    data_nascimento: d10(pessoa.data_nascimento),
    endereco: pessoa.endereco || '',
    contato_emergencia: pessoa.contato_emergencia || '',
    tipo_sanguineo: pessoa.tipo_sanguineo || '',
    alergias: pessoa.alergias || '',
    admissao: d10(pessoa.admissao),
  });
  const [salvando, setSalvando] = useState(false);
  const set = (k: keyof typeof f, v: string) => setF((prev) => ({ ...prev, [k]: v }));

  async function salvar() {
    setSalvando(true);
    try {
      const r = await acaoRH<{ pessoa: any; aviso?: string }>('rh_ficha_salvar', {
        profissional_id: pessoa.id, ...f,
      });
      message.success('Ficha salva.');
      // Aviso do backend (ex.: desligamento é data documental, não arquiva).
      if (r?.aviso) message.warning(r.aviso, 8);
      onSaved();
    } catch (e: any) {
      // 400 CPF/data inválida, 409 CPF duplicado — a mensagem do backend diz o quê.
      message.error('Não foi possível salvar: ' + msgErro(e));
    } finally { setSalvando(false); }
  }

  const campoData = (label: string, k: 'data_nascimento' | 'admissao') => (
    <div style={{ flex: 1, minWidth: 170 }}>
      <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>
      <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" allowClear
        value={f[k] ? dayjs(f[k]) : null}
        onChange={(d) => set(k, d ? d.format('YYYY-MM-DD') : '')} />
    </div>
  );

  const campoTexto = (label: string, k: keyof typeof f, placeholder?: string) => (
    <div style={{ flex: 1, minWidth: 200 }}>
      <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>
      <Input value={f[k]} onChange={(e) => set(k, e.target.value)} placeholder={placeholder} />
    </div>
  );

  return (
    <Modal open onCancel={() => !salvando && onClose()} onOk={salvar} confirmLoading={salvando}
      title={`Editar ficha — ${pessoa.nome}`} okText="Salvar" cancelText="Cancelar"
      // Regra da casa: modal usa a tela que houver, sem rolagem forçada.
      width="94vw" style={{ maxWidth: 680, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' } }}
      destroyOnClose>
      <Flex vertical gap={10} style={{ marginTop: 8 }}>
        <Flex gap={8} wrap>
          {campoTexto('Cargo', 'cargo', 'Engenheira, Técnico de campo...')}
        </Flex>
        <Flex gap={8} wrap>
          {campoTexto('Tipo de contratação', 'contrato_tipo', 'CLT, PJ, Estágio, Sócio...')}
          {campoTexto('Regime', 'regime', 'Integral, meio período...')}
        </Flex>
        <Flex gap={8} wrap>
          <div style={{ flex: 1, minWidth: 200 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>CPF</Text>
            <Input value={f.cpf} onChange={(e) => set('cpf', e.target.value)}
              placeholder="Só os números ou com pontos — o sistema confere os dígitos" />
          </div>
          {campoData('Nascimento', 'data_nascimento')}
        </Flex>
        {campoTexto('Endereço', 'endereco')}
        {campoTexto('Contato de emergência', 'contato_emergencia', 'Nome e telefone de quem avisar')}
        <Flex gap={8} wrap>
          <div style={{ flex: 1, minWidth: 170 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Tipo sanguíneo</Text>
            <Select style={{ width: '100%' }} value={f.tipo_sanguineo || undefined} allowClear
              placeholder="A+, O-, ..."
              onChange={(v) => set('tipo_sanguineo', v || '')}
              options={TIPOS_SANGUINEOS.map((t) => ({ label: t, value: t }))} />
          </div>
          {campoTexto('Alergias', 'alergias', 'Vazio = nenhuma conhecida')}
        </Flex>
        <Flex gap={8} wrap>
          {campoData('Admissão', 'admissao')}
        </Flex>
        <Text type="secondary" style={{ fontSize: 11 }}>
          A admissão é a base do cálculo de férias. Setor e chefe direto se editam no bloco
          Vínculo da ficha (organograma); a saída é pelo botão Desligar — os dois só pelo
          administrador do sistema.
        </Text>
      </Flex>
    </Modal>
  );
}

// ── Modal de período de férias (rh_ferias_salvar) ──────────────────────────
function ModalPeriodoFerias({ profissionalId, periodo, mostrarRemuneracao, onClose, onSaved }: {
  profissionalId: string;
  periodo: PeriodoFerias | null;      // null = novo
  mostrarRemuneracao: boolean;
  onClose: () => void;
  onSaved: (espelho: { marcados: number; pulados: number; motivo?: string }) => void;
}): ReactElement {
  const { message } = AntApp.useApp();
  const d10 = (v: string | null | undefined) => (v ? String(v).slice(0, 10) : '');
  const [inicio, setInicio] = useState(d10(periodo?.inicio));
  const [fim, setFim] = useState(d10(periodo?.fim));
  const [dias, setDias] = useState<number | null>(
    periodo ? (Number(periodo.dias) || null) : null);
  // Enquanto a pessoa não mexe no campo, os dias seguem o intervalo escolhido
  // (fim − início + 1); mexeu, o valor dela vale.
  const [diasEditado, setDiasEditado] = useState(!!periodo);
  const [observacao, setObservacao] = useState(periodo?.observacao || '');
  const [remuneracao, setRemuneracao] = useState<number | null>(
    periodo?.remuneracao !== undefined && periodo?.remuneracao !== null
      ? Number(periodo.remuneracao) : null);
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    if (!inicio || !fim) { message.warning('Escolha o início e o fim do período'); return; }
    if (!dias || dias <= 0) { message.warning('Os dias precisam ser maiores que zero'); return; }
    setSalvando(true);
    try {
      const r = await acaoRH<{ id: string; espelho: { marcados: number; pulados: number; motivo?: string } }>(
        'rh_ferias_salvar', {
          profissional_id: profissionalId, inicio, fim, dias,
          observacao: observacao.trim() || null,
          ...(mostrarRemuneracao ? { remuneracao } : {}),
          ...(periodo ? { id: periodo.id } : {}),
        });
      onSaved(r.espelho);
    } catch (e: any) {
      // 409 = sobreposição com outro período — a mensagem do backend explica.
      message.error('Não foi possível salvar: ' + msgErro(e));
    } finally { setSalvando(false); }
  }

  return (
    <Modal open onCancel={() => !salvando && onClose()} onOk={salvar} confirmLoading={salvando}
      title={periodo ? 'Editar período de férias' : 'Registrar período de férias'}
      okText="Salvar" cancelText="Cancelar"
      width="94vw" style={{ maxWidth: 560, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' } }}
      destroyOnClose>
      <Flex vertical gap={10} style={{ marginTop: 8 }}>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>Período</Text>
          <RangePicker style={{ width: '100%' }} format="DD/MM/YYYY"
            value={inicio && fim ? [dayjs(inicio), dayjs(fim)] : null}
            onChange={(v) => {
              const ini = v?.[0] ? v[0].format('YYYY-MM-DD') : '';
              const f = v?.[1] ? v[1].format('YYYY-MM-DD') : '';
              setInicio(ini); setFim(f);
              if (!diasEditado && ini && f) setDias(diasCorridos(ini, f));
            }} />
        </div>
        <Flex gap={8} wrap>
          <div style={{ minWidth: 140 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Dias (do saldo)</Text>
            <InputNumber style={{ width: '100%' }} min={0.5} step={0.5}
              value={dias}
              onChange={(v) => { setDias(v === null || v === undefined ? null : Number(v)); setDiasEditado(true); }} />
            <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
              Preenchido com os dias corridos do período — ajuste se a regra for outra.
            </div>
          </div>
          {mostrarRemuneracao && (
            <div style={{ minWidth: 180 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Remuneração no período (R$)</Text>
              <InputNumber style={{ width: '100%' }} min={0} precision={2}
                value={remuneracao}
                onChange={(v) => setRemuneracao(v === null || v === undefined ? null : Number(v))} />
            </div>
          )}
        </Flex>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>Observação (opcional)</Text>
          <Input value={observacao} onChange={(e) => setObservacao(e.target.value)} />
        </div>
        <Text type="secondary" style={{ fontSize: 11 }}>
          Ao salvar, os dias úteis do período viram ausência na cobrança de horas
          (feriados e dias já apontados ficam de fora).
        </Text>
      </Flex>
    </Modal>
  );
}

// ── Aba Documentos ─────────────────────────────────────────────────────────
function AbaDocumentos({ ficha, profissionalId, aoMudar }: {
  ficha: Ficha;
  profissionalId: string;
  aoMudar: () => void;
}): ReactElement {
  const { message } = AntApp.useApp();
  const bp = Grid.useBreakpoint();
  const [docs, setDocs] = useState<Documento[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [incluirArquivados, setIncluirArquivados] = useState(false);
  const [subcategorias, setSubcategorias] = useState<SubcategoriaDoc[]>([]);
  const [novo, setNovo] = useState(false);
  const [editando, setEditando] = useState<Documento | null>(null);
  const [motivoArquivar, setMotivoArquivar] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      // `profissional_id` existe no backend mas não no tipo FiltrosDoc da
      // api.ts (que é do acervo geral) — a interseção passa o campo sem `any`.
      const f: FiltrosDoc & { profissional_id?: string } = {
        categoria_codigo: 'rh', profissional_id: profissionalId,
      };
      if (incluirArquivados) f.incluir_arquivados = true;
      const r = await listarDocumentos(f);
      setDocs(r.documentos || []);
      setErro(null);
    } catch (e: any) {
      setDocs([]);
      setErro(e?.response?.status === 403
        ? 'Você não tem acesso aos documentos de RH'
        : 'Não foi possível carregar os documentos.');
    } finally { setCarregando(false); }
  }, [profissionalId, incluirArquivados]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    (async () => {
      try {
        const r = await listarSubcategorias('rh');
        setSubcategorias(r.subcategorias || []);
      } catch { /* sem tipos o formulário fica sem opções; a aba segue */ }
    })();
  }, []);

  const recarregar = useCallback(() => { carregar(); aoMudar(); }, [carregar, aoMudar]);

  // O ModalEditarDocRH pede a lista de pessoas para o Select — na ficha só há
  // uma, e trocar o vínculo aqui seria mover o documento de pessoa sem querer.
  const pessoaComoRH: PessoaRH[] = useMemo(() => [{
    id: ficha.pessoa.id, nome: ficha.pessoa.nome, ativo: ficha.pessoa.ativo,
    docs: Number(ficha.docs.total) || 0, pendencias: ficha.docs.pendencias || [],
  }], [ficha]);

  const cols = colunasDocsRH({
    mostrarPessoa: false,
    mapaPessoas: new Map<string, PessoaRH>(),
    podeGerenciar: ficha.pode_gerenciar,
    onBaixar: (row) => baixarDocRH(row, message),
    onEditar: (row) => setEditando(row),
    onArquivar: (row) => arquivarDocRH({
      row, para: true, motivo: motivoArquivar, message, aoMudar: recarregar,
    }),
    onDesarquivar: (row) => arquivarDocRH({
      row, para: false, message, aoMudar: recarregar,
    }),
    motivoArquivar, setMotivoArquivar,
  });

  // Piso da tabela: soma das colunas visíveis + espaço para o Título.
  const larguraMinima = useMemo(() => {
    let soma = 150 /* Tipo */ + 175 /* Validade */
      + (ficha.pode_gerenciar ? 122 : 56) /* Ações */;
    if (bp.lg) soma += 92; // Emissão
    return soma + 230;     // Título mínimo
  }, [bp.lg, ficha.pode_gerenciar]);

  const pend = ficha.docs.pendencias || [];

  return (
    <div>
      <Flex gap={8} align="center" wrap style={{ marginBottom: 10 }}>
        <Statistic title="Documentos" value={Number(ficha.docs.total) || 0}
          valueStyle={{ fontSize: 18 }} />
        <div style={{ flex: 1, minWidth: 160 }}>
          {pend.length > 0
            ? (
              <Space size={4} wrap>
                {pend.map((p, i) => (
                  <Tag key={i} color={p.situacao === 'vencendo' ? 'orange' : 'red'}>
                    {fmtPendencia(p)}
                  </Tag>
                ))}
              </Space>
            )
            : <Text type="secondary" style={{ fontSize: 12 }}>Nenhuma pendência na régua do obrigatório.</Text>}
        </div>
        <Tooltip title="Documento arquivado é papel substituído: sai do placar, mas continua no sistema">
          <Space size={4}>
            <Switch size="small" checked={incluirArquivados} onChange={setIncluirArquivados} />
            <Text style={{ fontSize: 12 }}>Arquivados</Text>
          </Space>
        </Tooltip>
        {ficha.pode_gerenciar && (
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setNovo(true)}>
            Novo documento
          </Button>
        )}
      </Flex>

      <Table<Documento>
        rowKey="id" columns={cols} dataSource={docs} loading={carregando}
        tableLayout="fixed" scroll={{ x: larguraMinima }}
        size="small" pagination={{ pageSize: 20 }}
        onRow={(record) => (ficha.pode_gerenciar
          ? { onClick: () => setEditando(record), style: { cursor: 'pointer' } }
          : {})}
        locale={{
          emptyText: <Empty description={erro
            || 'Nenhum documento desta pessoa. Cadastre contrato, ASO, EPI, CNH, currículo e certificados.'} />,
        }}
      />

      {novo && (
        <ModalNovoDocRH
          pessoaFixa={{ id: ficha.pessoa.id, nome: ficha.pessoa.nome }}
          subcategorias={subcategorias}
          onClose={() => setNovo(false)}
          onSaved={() => { setNovo(false); recarregar(); }} />
      )}
      {editando && (
        <ModalEditarDocRH
          doc={editando} pessoas={pessoaComoRH} subcategorias={subcategorias}
          onClose={() => setEditando(null)}
          onSaved={() => { setEditando(null); recarregar(); }} />
      )}
    </div>
  );
}

// ── Aba Férias ─────────────────────────────────────────────────────────────
function AbaFerias({ ficha, profissionalId, mostrarRemuneracao, aoMudar }: {
  ficha: Ficha;
  profissionalId: string;
  mostrarRemuneracao: boolean;
  aoMudar: () => void;
}): ReactElement {
  const { message } = AntApp.useApp();
  const [modal, setModal] = useState<{ periodo: PeriodoFerias | null } | null>(null);

  const semAdmissao = ficha.ferias.direito === null || ficha.ferias.direito === undefined;
  const periodos = ficha.ferias.periodos || [];
  // A coluna Remuneração só existe quando o backend mandou o campo (gate do
  // financeiro decide no servidor, interpolando a lista de colunas do SQL).
  const temColunaRemuneracao = mostrarRemuneracao
    || periodos.some((p) => Object.prototype.hasOwnProperty.call(p, 'remuneracao'));

  async function excluir(p: PeriodoFerias) {
    try {
      const r = await acaoRH<{ excluido: boolean; espelho_removidos: number }>(
        'rh_ferias_excluir', { id: p.id });
      const n = Number(r.espelho_removidos) || 0;
      message.success(n > 0
        ? `Período excluído — ${n} dia(s) de ausência removido(s) da cobrança de horas.`
        : 'Período excluído.');
      aoMudar();
    } catch (e: any) {
      message.error('Não foi possível excluir: ' + msgErro(e));
    }
  }

  function aoSalvarPeriodo(espelho: { marcados: number; pulados: number; motivo?: string }) {
    setModal(null);
    if (espelho.motivo === 'sem_usuario_email') {
      message.warning('Férias registradas, mas a pessoa não tem e-mail de login vinculado — '
        + 'a cobrança de horas não saberá dessas férias. Vincule o e-mail na aba Rateio & custo.', 10);
    } else {
      const partes = [`${espelho.marcados} dia(s) útil(eis) marcados como ausência na cobrança de horas`];
      if (espelho.pulados > 0) partes.push(`${espelho.pulados} pulado(s) por já ter apontamento ou registro`);
      message.success(`Férias salvas: ${partes.join('; ')}.`, 8);
    }
    aoMudar();
  }

  const cols: ColumnsType<PeriodoFerias> = [
    { title: 'Início', dataIndex: 'inicio', width: 100,
      render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{fmtDataBr(v)}</span> },
    { title: 'Fim', dataIndex: 'fim', width: 100,
      render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{fmtDataBr(v)}</span> },
    { title: 'Dias', dataIndex: 'dias', width: 70, align: 'right',
      render: (v) => fmtDias(v) },
    ...(temColunaRemuneracao ? [{
      title: 'Remuneração', dataIndex: 'remuneracao', width: 120, align: 'right' as const,
      render: (v: any) => brl(v),
    }] : []),
    { title: 'Observação', dataIndex: 'observacao',
      render: (v: string | null) => v || <Text type="secondary">—</Text> },
    ...(ficha.pode_editar_ficha ? [{
      title: 'Ações', key: 'acoes', width: 90, align: 'center' as const,
      render: (_: any, p: PeriodoFerias) => (
        <Space size={4}>
          <Button size="small" type="text" icon={<EditOutlined />} title="Editar"
            onClick={() => setModal({ periodo: p })} />
          <Popconfirm title="Excluir este período?"
            description="Os dias de ausência marcados por ele saem da cobrança de horas."
            okText="Excluir" okButtonProps={{ danger: true }} cancelText="Não"
            onConfirm={() => excluir(p)}>
            <Button size="small" type="text" danger icon={<DeleteOutlined />} title="Excluir" />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  return (
    <div>
      <Flex gap={24} align="center" wrap style={{ marginBottom: 12 }}>
        <Statistic title="Direito" value={semAdmissao ? '—' : fmtDias(ficha.ferias.direito)}
          suffix={semAdmissao ? undefined : 'dias'} valueStyle={{ fontSize: 20 }} />
        <Statistic title="Gozados / agendados" value={fmtDias(ficha.ferias.gozados)}
          suffix="dias" valueStyle={{ fontSize: 20 }} />
        <Statistic title="Saldo" value={semAdmissao ? '—' : fmtDias(ficha.ferias.saldo)}
          suffix={semAdmissao ? undefined : 'dias'}
          valueStyle={{ fontSize: 20, color: !semAdmissao && Number(ficha.ferias.saldo) < 0 ? '#B91626' : undefined }} />
        <div style={{ flex: 1 }} />
        {ficha.pode_editar_ficha && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModal({ periodo: null })}>
            Registrar período
          </Button>
        )}
      </Flex>

      {semAdmissao && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="Sem data de admissão não dá para calcular o direito"
          description="Preencha a admissão na ficha (botão Editar ficha) — o direito é de 2,5 dias por mês trabalhado." />
      )}

      <Table<PeriodoFerias>
        rowKey="id" columns={cols} dataSource={periodos}
        size="small" pagination={false} tableLayout="fixed"
        scroll={{ x: 100 + 100 + 70 + (temColunaRemuneracao ? 120 : 0) + (ficha.pode_editar_ficha ? 90 : 0) + 180 }}
        locale={{ emptyText: <Empty description="Nenhum período de férias registrado." /> }}
      />

      {modal && (
        <ModalPeriodoFerias
          profissionalId={profissionalId}
          periodo={modal.periodo}
          mostrarRemuneracao={temColunaRemuneracao}
          onClose={() => setModal(null)}
          onSaved={aoSalvarPeriodo} />
      )}
    </div>
  );
}

// ── Aba Horas ──────────────────────────────────────────────────────────────
function AbaHoras({ pessoa }: { pessoa: PessoaFicha }): ReactElement {
  const [linha, setLinha] = useState<CoberturaPessoa | null>(null);

  const email = pessoa.usuario_email;

  useEffect(() => {
    if (!email) return;
    let vivo = true;
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.get(
          `${API_URL}/financeiro/horas/cobertura?competencia=${dayjs().format('YYYY/MM')}`,
          { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        if (!vivo) return;
        const pessoas: CoberturaPessoa[] = d.pessoas || [];
        setLinha(pessoas.find((p) => String(p.profissional_id) === String(pessoa.id)
          || (p.usuario_email || '').toLowerCase() === email.toLowerCase()) || null);
      } catch {
        // Sem acesso (403) ou erro: os dois números somem; as horas abaixo continuam.
        if (vivo) setLinha(null);
      }
    })();
    return () => { vivo = false; };
  }, [email, pessoa.id]);

  if (!email) {
    return (
      <Empty description={
        <span>
          Pessoa sem e-mail de login — vincule na aba <strong>Rateio &amp; custo</strong> para
          as horas dela aparecerem aqui e virarem custo de contrato.
        </span>
      } />
    );
  }

  return (
    <div>
      {linha && (
        <Flex gap={24} wrap style={{ marginBottom: 12 }}>
          <Statistic title="Apontado no mês" value={linha.minutos}
            formatter={() => <TotalHoras minutos={linha.minutos} alinhar="start" tamanhoDecimal={12} />}
            valueStyle={{ fontSize: 20 }} />
          <Statistic title="Ausências" value={linha.dias_ausencia} suffix="dias"
            valueStyle={{ fontSize: 20 }} />
        </Flex>
      )}
      <MinhaSemana emailAlvo={email} />
    </div>
  );
}

// ── A ficha ────────────────────────────────────────────────────────────────
type AbaKey = 'docs' | 'ferias' | 'horas' | 'remuneracao' | 'rateio';

export default function FichaProfissional({ profissionalId, aoVoltar, recursos, onRecarregar }: {
  profissionalId: string;
  aoVoltar: () => void;
  recursos: RecursosFicha;
  onRecarregar: () => void;
}): ReactElement {
  const { message, modal } = AntApp.useApp();
  const [ficha, setFicha] = useState<Ficha | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<'sem-acesso' | 'nao-encontrado' | string | null>(null);
  const [aba, setAba] = useState<AbaKey>('docs');
  const [editandoFicha, setEditandoFicha] = useState(false);

  const carregarFicha = useCallback(async () => {
    setCarregando(true);
    try {
      const d = await acaoRH<Ficha>('rh_ficha', { profissional_id: profissionalId });
      setFicha(d);
      setErro(null);
    } catch (e: any) {
      setFicha(null);
      const st = e?.response?.status;
      setErro(st === 403 ? 'sem-acesso' : st === 404 ? 'nao-encontrado' : msgErro(e));
    } finally { setCarregando(false); }
  }, [profissionalId]);

  useEffect(() => { carregarFicha(); }, [carregarFicha]);

  // Depois de qualquer escrita: a ficha recarrega e a lista da Equipe também
  // (cargo, admissão, saldo e pendências aparecem lá).
  const aoMudar = useCallback(() => { carregarFicha(); onRecarregar(); }, [carregarFicha, onRecarregar]);

  // O objeto do GET /profissionais — é ele que carrega o sinal de permissão
  // financeira (presença da chave salario_esperado) e alimenta a SecaoCadastro.
  const profissional = useMemo(
    () => recursos.profissionais.find((p) => String(p.id) === String(profissionalId)),
    [recursos.profissionais, profissionalId]);
  const temPermFin = !!profissional && 'salario_esperado' in profissional;

  async function mudarAtivo(novoAtivo: boolean) {
    try {
      const token = await getToken();
      // Mesmo endpoint da tela antiga de profissionais (dre-engine).
      await axios.put(`${API_URL}/profissionais/${profissionalId}`,
        { ativo: novoAtivo }, { headers: { Authorization: token } });
      message.success(novoAtivo
        ? 'Profissional reativado.'
        : 'Profissional arquivado (ex-funcionário).');
      aoMudar();
    } catch (e: any) {
      message.error('Não foi possível: ' + msgErro(e));
    }
  }

  if (carregando && !ficha) {
    return <Flex justify="center" style={{ padding: 48 }}><Spin /></Flex>;
  }

  if (erro === 'sem-acesso') {
    return (
      <div style={{ padding: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={aoVoltar} style={{ marginBottom: 12 }}>
          Equipe
        </Button>
        <Alert type="warning" showIcon message="Você não tem acesso à ficha desta pessoa" />
      </div>
    );
  }

  if (erro === 'nao-encontrado' || (!ficha && erro)) {
    return (
      <div style={{ padding: 16 }}>
        <Empty description={erro === 'nao-encontrado'
          ? 'Profissional não encontrado — pode ter sido removido.'
          : `Não foi possível abrir a ficha: ${erro}`}>
          <Button type="primary" icon={<ArrowLeftOutlined />} onClick={aoVoltar}>
            Voltar para a equipe
          </Button>
        </Empty>
      </div>
    );
  }

  if (!ficha) return <Flex justify="center" style={{ padding: 48 }}><Spin /></Flex>;

  const p = ficha.pessoa;
  const semAdmissao = ficha.ferias.direito === null || ficha.ferias.direito === undefined;

  return (
    <div style={{ padding: 16 }}>
      <Row gutter={16} align="top">
        {/* ─── ESQUERDA: a pessoa ─── */}
        <Col xs={24} md={8} style={{ minWidth: 0 }}>
          <Card size="small" styles={{ body: { padding: 0 } }}>
            <div style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>
              <Button size="small" icon={<ArrowLeftOutlined />} onClick={aoVoltar}
                style={{ marginBottom: 8 }}>
                Equipe
              </Button>
              <Flex align="center" gap={8} wrap>
                <Title level={5} style={{ margin: 0 }}>{p.nome}</Title>
                {p.ativo
                  ? <Tag color="green">ativo</Tag>
                  : <Tag>desligado</Tag>}
              </Flex>
              {p.cargo && <Text type="secondary" style={{ fontSize: 12 }}>{p.cargo}</Text>}
            </div>

            <Secao titulo="Dados pessoais">
              <Descriptions {...descProps}>
                <Descriptions.Item label="CPF">{fmtCpf(p.cpf)}</Descriptions.Item>
                <Descriptions.Item label="Nascimento">{fmtDataBr(p.data_nascimento)}</Descriptions.Item>
              </Descriptions>
            </Secao>

            <Secao titulo="Contato">
              <Descriptions {...descProps}>
                <Descriptions.Item label="Endereço">{p.endereco || '—'}</Descriptions.Item>
                <Descriptions.Item label="Emergência">{p.contato_emergencia || '—'}</Descriptions.Item>
              </Descriptions>
            </Secao>

            <Secao titulo="Saúde">
              <Descriptions {...descProps}>
                <Descriptions.Item label="Sangue">{p.tipo_sanguineo || '—'}</Descriptions.Item>
                <Descriptions.Item label="Alergias">{p.alergias || '—'}</Descriptions.Item>
              </Descriptions>
            </Secao>

            <Secao titulo="Vínculo">
              <Descriptions {...descProps}>
                <Descriptions.Item label="Admissão">{fmtDataBr(p.admissao)}</Descriptions.Item>
                {p.desligamento
                  ? <Descriptions.Item label="Desligamento">{fmtDataBr(p.desligamento)}</Descriptions.Item>
                  : null}
                <Descriptions.Item label="Contratação">{p.contrato_tipo || '—'}</Descriptions.Item>
                <Descriptions.Item label="Regime">{p.regime || '—'}</Descriptions.Item>
                <Descriptions.Item label="Login">{p.usuario_email || '—'}</Descriptions.Item>
              </Descriptions>
              {/* Organograma funcional (28/09/2026): setor e chefe direto — quem
                  recebe os alertas de horas desta pessoa. Edição só do admin. */}
              <div style={{ marginTop: 6 }}>
                <OrganogramaCampos profissionalId={String(profissionalId)}
                  setorId={p.setor_id} setorNome={p.setor}
                  chefeId={p.chefe_profissional_id} chefeNome={p.chefe_nome}
                  aoSalvar={aoMudar} />
              </div>
            </Secao>

            <Secao titulo="Férias">
              <Statistic title="Saldo de férias"
                value={semAdmissao ? '—' : fmtDias(ficha.ferias.saldo)}
                suffix={semAdmissao ? undefined : 'dias'}
                valueStyle={{
                  fontSize: 20,
                  color: !semAdmissao && Number(ficha.ferias.saldo) < 0 ? '#B91626' : '#020D7F',
                }} />
              {semAdmissao && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Sem data de admissão não há cálculo — preencha na ficha.
                </Text>
              )}
            </Secao>

            <Secao titulo="Ações" ultima>
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                {ficha.pode_editar_ficha && (
                  <Button block icon={<EditOutlined />} onClick={() => setEditandoFicha(true)}>
                    Editar ficha
                  </Button>
                )}
                {p.ativo ? (
                  <Button block danger type="text" size="small"
                    onClick={() => modal.confirm({
                      title: `Arquivar ${p.nome}?`,
                      content: 'Marca como ex-funcionário: sai das listas e da régua de documentos. Os lançamentos antigos ficam intocados, e dá para reativar a qualquer momento.',
                      okText: 'Arquivar', cancelText: 'Voltar',
                      onOk: () => mudarAtivo(false),
                    })}>
                    Arquivar (marcar como ex-funcionário)
                  </Button>
                ) : (
                  <Button block type="text" size="small" onClick={() => mudarAtivo(true)}>
                    Reativar
                  </Button>
                )}
                {p.atualizado_em && (
                  <Text type="secondary" style={{ fontSize: 10 }}>
                    Ficha atualizada em {fmtDataBr(p.atualizado_em)}
                    {p.atualizado_por ? ` por ${p.atualizado_por}` : ''}
                  </Text>
                )}
              </Space>
            </Secao>
          </Card>
        </Col>

        {/* ─── DIREITA: abas ─── */}
        <Col xs={24} md={16} style={{ minWidth: 0 }}>
          <Card size="small" styles={{ body: { padding: 0 } }}>
            <Tabs activeKey={aba} onChange={(k) => setAba(k as AbaKey)} size="small"
              style={{ padding: '0 12px' }}
              items={[
                { key: 'docs', label: 'Documentos' },
                { key: 'ferias', label: 'Férias' },
                { key: 'horas', label: 'Horas' },
                { key: 'remuneracao', label: 'Remuneração' },
                { key: 'rateio', label: 'Rateio & custo' },
              ]}
            />
            <div style={{ padding: 16 }}>
              {aba === 'docs' && (
                <AbaDocumentos ficha={ficha} profissionalId={profissionalId} aoMudar={aoMudar} />
              )}
              {aba === 'ferias' && (
                <AbaFerias ficha={ficha} profissionalId={profissionalId}
                  mostrarRemuneracao={temPermFin} aoMudar={aoMudar} />
              )}
              {aba === 'horas' && <AbaHoras pessoa={p} />}
              {aba === 'remuneracao' && (
                temPermFin
                  ? <HistoricoRemuneracao profissionalId={profissionalId} />
                  : (
                    <Alert type="info" showIcon
                      message="Você não tem acesso à remuneração"
                      description="Os valores de salário e coeficiente não são carregados para o seu perfil. O resto da ficha continua disponível." />
                  )
              )}
              {aba === 'rateio' && (
                profissional
                  ? (
                    <div>
                      <SecaoCadastro
                        profissional={profissional}
                        clockifyCfg={recursos.clockifyCfgMap[String(profissionalId)]}
                        clockifyUsers={recursos.clockifyUsers}
                        usuariosSistema={recursos.usuariosSistema}
                        todosProfissionais={recursos.profissionais}
                        aoSalvar={aoMudar}
                      />
                    </div>
                  )
                  : (
                    <Alert type="warning" showIcon
                      message="Cadastro de rateio indisponível"
                      description="A lista de profissionais não carregou — atualize a página de Equipe e tente de novo." />
                  )
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {editandoFicha && (
        <ModalEditarFicha
          pessoa={p}
          onClose={() => setEditandoFicha(false)}
          onSaved={() => { setEditandoFicha(false); aoMudar(); }} />
      )}
    </div>
  );
}
